import React, { useEffect, useState } from 'react';
import VendorStats, { VendorStatsType } from './vendor-stats';
import VendorDetails from './vendor-details';
import { BankAltIcon, RmxNumberIcon, PhoneIcon, UserIcon } from 'cokitchen-icons';
import { ReactComponent as TickIcon } from 'assets/icons/tick-icon.svg';
import TransactionHistory from './transaction-history';
import useDashboardContext from 'contexts/dashboard.context';
import PageHeaderText from 'components/base/page-header-text';
import { Button } from '@cokitchen/cokitchen-components';
import PaymentStatusTag from 'components/base/payment-status-tag';
import UpdatePaymentModal from 'components/modals/update-payment-modal';
import { UPDATE_PAYMENT_ACTIONS, PaymentRequestDetailType } from 'models/payment-tracker.model';
import { ENUM_PAYMENT_STATUS } from 'models/common.model';
import { useNavigate, useParams } from 'react-router-dom';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import { apiService } from 'services/base.service';
import { handleApiError } from 'utils/functions';

const VendorProfile = () => {
  const [modalType, setModalType] = useState<UPDATE_PAYMENT_ACTIONS>(UPDATE_PAYMENT_ACTIONS.NONE);
  const [paymentDetail, setPaymentDetail] = useState<PaymentRequestDetailType | null>(null);
  const [isFetchingPaymentDetail, setIsFetchingPaymentDetail] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  const { setIsBackLink } = useDashboardContext();

  const fetchVendorPaymentDetails = async () => {
    if (!id) {
      navigate(MAIN_STORE_ROUTE_KEYS.PAYMNET_TRACKER);
      return;
    }

    try {
      setIsFetchingPaymentDetail(true);
      const response = await apiService.get(`payment-request/${id}`, {
        version: 'v2'
      });

      if (response) {
        setPaymentDetail(response.data.data);
      }
    } catch (error) {
      handleApiError(error);
      navigate(MAIN_STORE_ROUTE_KEYS.PAYMNET_TRACKER);
    } finally {
      setIsFetchingPaymentDetail(false);
    }
  };

  useEffect(() => {
    setIsBackLink(true);
    return () => {
      setIsBackLink(false);
    };
  }, []);

  useEffect(() => {
    fetchVendorPaymentDetails();
  }, [id]);

  if (isFetchingPaymentDetail) {
    return (
      <div className='flex items-center justify-center h-[50vh]'>
        <div className='text-xl'>Loading...</div>
      </div>
    );
  }

  if (!paymentDetail) {
    return null;
  }

  const vendorStatsArray: VendorStatsType[] = [
    {
      title: 'Amount',
      value: paymentDetail.amount
    }
  ];

  const vendorDetailsArray = [
    {
      icon: <PhoneIcon />,
      label: 'Phone number',
      value: paymentDetail.vendor.phone_number
    },
    {
      icon: <BankAltIcon />,
      label: 'Bank account',
      value: paymentDetail.vendor.bank_name
    },
    {
      icon: <RmxNumberIcon />,
      label: 'Account number',
      value: paymentDetail.vendor.account_number
    },
    {
      icon: <UserIcon />,
      label: 'Account name',
      value: paymentDetail.vendor.account_name
    }
  ];

  return (
    <div>
      <div>
        <div className='flex justify-between'>
          <PageHeaderText
            title={paymentDetail.vendor.name}
            description={`#${paymentDetail.code || 'N/A'}`}
            tag={<PaymentStatusTag status={paymentDetail.status as ENUM_PAYMENT_STATUS} />}>
            <div className='flex gap-3 '>
              {paymentDetail.status === ENUM_PAYMENT_STATUS.REQUESTED ? (
                <Button
                  onClick={() => {
                    setModalType(UPDATE_PAYMENT_ACTIONS.APPROVE);
                  }}
                  variant='block'
                  color='primary'
                  className='w-[167px] text-white'
                  small>
                  Approve
                </Button>
              ) : null}

              {paymentDetail.status === ENUM_PAYMENT_STATUS.APPROVED ? (
                <Button
                  onClick={() => {
                    setModalType(UPDATE_PAYMENT_ACTIONS.COMPLETE);
                  }}
                  variant='block'
                  color='primary'
                  className='w-[197px] text-white'
                  small>
                  <span className='mr-2'>
                    <TickIcon />
                  </span>
                  Mark as paid
                </Button>
              ) : null}
              {paymentDetail.status === ENUM_PAYMENT_STATUS.REQUESTED ? (
                <Button
                  onClick={() => {
                    setModalType(UPDATE_PAYMENT_ACTIONS.REJECT);
                  }}
                  variant='block'
                  color='light-gray'
                  className='w-[167px] text-black'
                  small>
                  Reject
                </Button>
              ) : null}
            </div>
          </PageHeaderText>
        </div>
        <div className='space-y-8'>
          <div className='flex flex-col md:flex-row gap-4'>
            <div className='w-[50%] min-w-[250px]'>
              <VendorStats list={vendorStatsArray} />
            </div>
            <div className='w-full'>
              <VendorDetails list={vendorDetailsArray} />
            </div>
          </div>
          <TransactionHistory paymentDetail={paymentDetail} />
        </div>
      </div>
      <UpdatePaymentModal
        {...{
          modalType,
          isOpen: !!modalType,
          onRequestClose: () => {
            setModalType(UPDATE_PAYMENT_ACTIONS.NONE);
          }
        }}
      />
    </div>
  );
};

export default VendorProfile;
